import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client.ts';
import type { Billing, BillingCurrency, Limits, PaidTier, PriceKey, PublicPricing, SignupCountry, SignupPlan } from '../api/types.ts';
import { BrandPin, Icon, type IconName } from '../components/Icon.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { tierName } from '../components/UpgradePrompt.tsx';
import { Segmented } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { annualSaving, coachOffered, isUpgradeFeature, priceText, signupCountry } from '../lib/billing.ts';
import { useDocumentTitle } from '../lib/documentTitle.ts';
import { useIsDesktop } from '../lib/hooks.ts';
import { useStore } from '../state/store.tsx';

export const COACH_APPLY_URL = 'mailto:support@mapmymoney.app?subject=Coach%20plan';

/** Billing fetched afresh when a screen opens (it may have changed in checkout or the portal). */
export function useFreshBilling(): { billing: Billing | null; status: 'loading' | 'ready' | 'error'; retry: () => void } {
  const { billing, refreshBilling } = useStore();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(billing ? 'ready' : 'loading');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    void refreshBilling().then((fresh) => live && setStatus(fresh ? 'ready' : 'error'));
    return () => {
      live = false;
    };
  }, [refreshBilling, attempt]);
  return {
    billing,
    status: billing ? 'ready' : status,
    retry: () => {
      setStatus('loading');
      setAttempt((a) => a + 1);
    },
  };
}

/** Sends the browser to Stripe (checkout, or the portal for an existing subscription). */
export async function goToCheckout(plan: PriceKey, billing: Billing): Promise<string | null> {
  try {
    const { url } = await api.checkout(plan, billing.currency);
    window.location.assign(url);
    return null;
  } catch (error) {
    if (error instanceof ApiError && error.status === 503) return t('pricing.unavailable');
    return t('pricing.error');
  }
}

export function UpgradeScreen() {
  useDocumentTitle(t('docTitle.upgrade'));
  const desktop = useIsDesktop();
  const [params] = useSearchParams();
  const feature = params.get('feature');
  const { billing, status, retry } = useFreshBilling();
  const subtitle = billing?.lapsed && !isUpgradeFeature(feature) ? t('pricing.subtitle.lapsed') : t('pricing.subtitle');

  const header = desktop ? (
    <PageHeader eyebrow={t('pricing.eyebrow')} title={t('pricing.title')} subtitle={subtitle} />
  ) : (
    <header className="stack-xs">
      <Link to="/settings#billing" className="text-link text-link--icon pricing__back"><Icon name="chevronLeft" size={14} />{t('nav.settings')}</Link>
      <h1 className="mobile-title">{t('pricing.title')}</h1>
      <p className="page-subtitle">{subtitle}</p>
    </header>
  );

  return (
    <div className={`${desktop ? 'page' : 'page page--mobile'} pricing ${billing && coachOffered(billing) ? '' : 'pricing--single'}`}>
      {header}
      {isUpgradeFeature(feature) && (feature !== 'branding' || (billing && coachOffered(billing))) && (
        <p className="pricing__reason" role="note"><Icon name="sparkle" size={15} />{t(`pricing.reason.${feature}`)}</p>
      )}
      {status === 'ready' && billing ? (
        <Pricing billing={billing} />
      ) : status === 'error' ? (
        <PricingProblem retry={retry} />
      ) : (
        <PricingSkeleton />
      )}
    </div>
  );
}

function PricingProblem({ retry }: { retry: () => void }) {
  return (
    <div className="card card--pad-lg stack-md pricing__problem" role="alert">
      <p>{t('billing.loadFailed')}</p>
      <button type="button" className="button button--sm button--outline" onClick={retry}>{t('common.retry')}</button>
    </div>
  );
}

function PricingSkeleton() {
  return (
    <div className="tier-grid" aria-busy="true">
      <p className="sr-only" role="status">{t('common.loading')}</p>
      <div className="skeleton tier-card--skeleton" />
    </div>
  );
}

type Interval = 'month' | 'year';
type PlusPrices = PublicPricing['prices'];

const plusKeyFor = (interval: Interval): SignupPlan => (interval === 'month' ? 'plus_monthly' : 'plus_annual');

function IntervalToggle({ prices, value, onChange }: { prices: PlusPrices; value: Interval; onChange: (value: Interval) => void }) {
  const saving = annualSaving({ prices });
  return (
    <div className="pricing__interval">
      <Segmented
        label={t('pricing.interval')}
        variant="pill"
        options={[
          { value: 'month', label: t('pricing.monthly') },
          { value: 'year', label: saving > 0 ? `${t('pricing.annual')} · ${t('pricing.save', { pct: saving })}` : t('pricing.annual') },
        ]}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

/** Plus, the one plan on sale: its price for the chosen interval and what it includes. */
function PlusCard({ prices, limits, interval, action, current = false }: { prices: PlusPrices; limits: Limits; interval: Interval; action: ReactNode; current?: boolean }) {
  return (
    <TierCard
      tier="plus"
      limits={limits}
      featured
      current={current}
      tagline={t('pricing.plus.tagline')}
      price={interval === 'month' ? (
        <PriceLine amount={priceText(prices.plus_monthly)} per={t('pricing.perMonth')} />
      ) : (
        <PriceLine amount={priceText(prices.plus_annual)} per={t('pricing.perYear')} />
      )}
      note={interval === 'month' ? t('pricing.monthlyNote') : t('pricing.equivalent', { amount: priceText(prices.plus_annual, Number(prices.plus_annual.amount) / 12) })}
      action={action}
      extraFeatures={PLUS_BASICS}
    />
  );
}

function BuyButton({ label, busy, disabled, onClick }: { label: string; busy: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <button type="button" className="button button--primary button--glow button--block button--lg" disabled={disabled} onClick={onClick}>
      {busy ? t('pricing.redirecting') : label}
    </button>
  );
}

function Pricing({ billing }: { billing: Billing }) {
  const [interval, setInterval] = useState<Interval>(billing.interval === 'month' ? 'month' : 'year');
  const [busy, setBusy] = useState<PriceKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const checkout = async (plan: PriceKey) => {
    setBusy(plan);
    setError(null);
    const problem = await goToCheckout(plan, billing);
    if (problem) {
      setError(problem);
      setBusy(null);
    }
  };
  const plusKey = plusKeyFor(interval);
  const canBuy = billing.available && busy === null;
  const coach = coachOffered(billing);
  const plusLimits = billing.tiers.plus;

  return (
    <div className="pricing__body">
      {!billing.enforced && <p className="pricing__early"><Icon name="checkCircle" size={15} />{t('pricing.early')}</p>}
      {!billing.available && <p className="pricing__unavailable" role="note"><Icon name="lock" size={14} />{t('pricing.unavailable')}</p>}
      {error && <p className="share-new__error" role="alert"><Icon name="alert" size={15} />{error}</p>}
      <IntervalToggle prices={billing.prices} value={interval} onChange={setInterval} />
      <div className={`tier-grid ${coach ? 'tier-grid--two' : 'tier-grid--one'}`}>
        {plusLimits && (
          <PlusCard
            prices={billing.prices}
            limits={plusLimits}
            interval={interval}
            current={billing.tier === 'plus'}
            action={billing.tier === 'plus' ? <CurrentButton /> : billing.tier === 'coach' ? (
              <button type="button" className="button button--outline button--block" disabled>{t('pricing.included')}</button>
            ) : (
              <BuyButton
                label={billing.lapsed ? t('pricing.resubscribe') : t('pricing.start')}
                busy={busy === plusKey}
                disabled={!canBuy}
                onClick={() => void checkout(plusKey)}
              />
            )}
          />
        )}
        {coach && billing.prices.coach_annual && billing.tiers.coach && (
          <TierCard
            tier="coach"
            limits={billing.tiers.coach}
            current={billing.tier === 'coach'}
            tagline={t('pricing.coach.tagline')}
            price={<PriceLine amount={priceText(billing.prices.coach_annual)} per={t('pricing.perYear')} />}
            note={billing.coach === 'apply' ? t('pricing.coachByApplication') : t('pricing.coach.billing')}
            {...(billing.prices.coach_extra_client ? { clientPrice: priceText(billing.prices.coach_extra_client) } : {})}
            action={billing.coach === 'current' ? <CurrentButton /> : billing.coach === 'eligible' ? (
              <button type="button" className="button button--outline-mint button--block" disabled={!canBuy} onClick={() => void checkout('coach_annual')}>
                {busy === 'coach_annual' ? t('pricing.redirecting') : t('pricing.chooseCoach')}
              </button>
            ) : (
              <a className="button button--outline button--block" href={COACH_APPLY_URL}>
                <Icon name="chat" size={15} />{t('pricing.coachApply')}
              </a>
            )}
          />
        )}
      </div>
      <Reassurance />
    </div>
  );
}

/** /upgrade for someone without an account: the same offer, paid for before the account exists (guest checkout). */
export function PublicUpgradeScreen() {
  useDocumentTitle(t('docTitle.upgrade'));
  const [params] = useSearchParams();
  const country = signupCountry(params.get('country'));
  const [paidSignup, setPaidSignup] = useState<boolean | null>(null);
  const [pricing, setPricing] = useState<PublicPricing | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    api.authMethods().then((m) => live && setPaidSignup(m.paidSignup === true), () => live && setPaidSignup(false));
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (!paidSignup) return;
    let live = true;
    setFailed(false);
    api.pricing(country).then((p) => live && setPricing(p), () => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [paidSignup, country, attempt]);

  if (paidSignup === false) return <Navigate to="/signin?next=%2Fupgrade" replace />;
  return (
    <div className="public-page">
      <header className="public-page__bar">
        <Link to="/signin" className="public-page__brand" aria-label={t('app.name')}><BrandPin size={26} /><span>{t('app.name')}</span></Link>
        <Link to="/signin" className="button button--sm button--ghost">{t('signin.submitSignIn')}</Link>
      </header>
      <main className="page pricing pricing--single pricing--public" id="main">
        <header className="pricing__hero stack-xs">
          <p className="eyebrow">{t('pricing.eyebrow')}</p>
          <h1 className="pricing__title">{t('pricing.title')}</h1>
          <p className="page-subtitle">{t('pricing.subtitle')}</p>
        </header>
        <section className="pricing-start" aria-labelledby="pricing-start" data-testid="pricing-start">
          <div>
            <h2 className="pricing-start__title" id="pricing-start">{t('startEntry.pricing.title')}</h2>
            <p className="pricing-start__body">{t('startEntry.pricing.body')}</p>
          </div>
          <Link to="/start" className="button button--sm button--outline-mint">{t('startEntry.pricing.cta')}<Icon name="arrowRight" size={14} /></Link>
        </section>
        {pricing ? (
          <PublicPricingBody pricing={pricing} country={country} />
        ) : failed ? (
          <PricingProblem retry={() => setAttempt((a) => a + 1)} />
        ) : (
          <PricingSkeleton />
        )}
      </main>
    </div>
  );
}

function PublicPricingBody({ pricing, country }: { pricing: PublicPricing; country: SignupCountry | null }) {
  const [interval, setInterval] = useState<Interval>('year');
  const [busy, setBusy] = useState<SignupPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const plan = plusKeyFor(interval);
  const start = async () => {
    setBusy(plan);
    setError(null);
    const problem = await goToSignupCheckout(plan, pricing.currency, country);
    if (problem) {
      setError(problem);
      setBusy(null);
    }
  };
  return (
    <div className="pricing__body">
      {!pricing.available && <p className="pricing__unavailable" role="note"><Icon name="lock" size={14} />{t('pricing.unavailable')}</p>}
      {error && <p className="share-new__error" role="alert"><Icon name="alert" size={15} />{error}</p>}
      <IntervalToggle prices={pricing.prices} value={interval} onChange={setInterval} />
      <div className="tier-grid tier-grid--one">
        <PlusCard
          prices={pricing.prices}
          limits={pricing.limits}
          interval={interval}
          action={<BuyButton label={t('pricing.start')} busy={busy === plan} disabled={!pricing.available || busy !== null} onClick={() => void start()} />}
        />
      </div>
      <p className="pricing__signin"><Link to="/signin" className="text-link">{t('pricing.signedOut.signIn')}</Link></p>
      <Reassurance />
    </div>
  );
}

async function goToSignupCheckout(plan: SignupPlan, currency: BillingCurrency, country: SignupCountry | null): Promise<string | null> {
  try {
    const { url } = await api.signupCheckout(plan, currency, country);
    window.location.assign(url);
    return null;
  } catch (error) {
    if (error instanceof ApiError && error.status === 503) return t('pricing.unavailable');
    if (error instanceof ApiError && error.status === 429) return t('signin.rateLimited');
    return t('pricing.error');
  }
}

function Reassurance() {
  return (
    <>
      <div className="pricing__honest">
        <AlwaysFree />
        <section className="card card--pad-lg stack-sm" aria-labelledby="pricing-downgrade">
          <h2 className="section-title" id="pricing-downgrade">{t('pricing.downgrade.title')}</h2>
          <p className="muted-2 pricing__para">{t('pricing.downgrade.body')}</p>
        </section>
      </div>
      <p className="fine-print pricing__vat">{t('pricing.vat')}</p>
    </>
  );
}

function CurrentButton() {
  return <button type="button" className="button button--outline button--block tier-card__current" disabled>{t('pricing.current')}</button>;
}

function PriceLine({ amount, per }: { amount: string; per?: string }) {
  return (
    <p className="tier-card__price">
      <span className="tier-card__amount">{amount}</span>
      {per && <span className="tier-card__per">{per}</span>}
    </p>
  );
}

interface Feature {
  readonly icon: IconName;
  readonly text: string;
  readonly muted?: boolean;
}

const PLUS_BASICS: readonly Feature[] = [
  { icon: 'trend', text: t('pricing.feature.core') },
  { icon: 'chat', text: t('pricing.feature.assistant') },
];

function features(limits: Limits, clientPrice: string | null): Feature[] {
  const limited = (n: number | null, some: MessageKey, any: MessageKey) => (n === null ? t(any) : t(some, { n }));
  const seats = limits.editorSeatsPerPlan;
  const editors = seats === 0
    ? t('pricing.feature.viewersOnly')
    : seats === 1 ? t('pricing.feature.editorsOne') : limited(seats, 'pricing.feature.editors', 'pricing.feature.editorsAny');
  const list: Feature[] = [
    { icon: 'plans', text: limited(limits.ownedPlans, 'pricing.feature.plans', 'pricing.feature.plansAny') },
    { icon: 'link', text: limited(limits.liveShares, 'pricing.feature.shares', 'pricing.feature.sharesAny') },
    { icon: 'people', text: limited(limits.peoplePerPlan, 'pricing.feature.people', 'pricing.feature.peopleAny') },
    { icon: seats === 0 ? 'eye' : 'pen', text: editors },
    limits.customShareExpiry
      ? { icon: 'check', text: t('pricing.feature.expiry') }
      : { icon: 'check', text: t('pricing.feature.expiryStandard'), muted: true },
  ];
  if (limits.brandedShares) list.push({ icon: 'sparkle', text: t('pricing.feature.branding') });
  if (limits.includedClients !== null && clientPrice) list.push({ icon: 'people', text: t('pricing.feature.clients', { n: limits.includedClients, price: clientPrice }) });
  return list;
}

function TierCard({ tier, limits, tagline, price, note, action, current = false, featured = false, extraFeatures = [], clientPrice = null }: {
  tier: PaidTier;
  limits: Limits;
  tagline: string;
  price: ReactNode;
  note: string;
  action: ReactNode;
  current?: boolean;
  featured?: boolean;
  extraFeatures?: readonly Feature[];
  clientPrice?: string | null;
}) {
  const id = useId();
  return (
    <section
      className={`tier-card tier-card--${tier} ${featured ? 'tier-card--featured' : ''} ${current ? 'is-current' : ''}`}
      aria-labelledby={id}
      {...(current ? { 'aria-current': 'true' as const } : {})}
    >
      <div className="tier-card__head">
        <h2 className="tier-card__name" id={id}>{tierName(tier)}</h2>
        {current && <span className="tier-card__badge">{t('pricing.currentBadge')}</span>}
      </div>
      <p className="tier-card__tagline">{tagline}</p>
      {price}
      <p className="tier-card__note">{note}</p>
      <div className="tier-card__action">{action}</div>
      <ul className="tier-card__features">
        {[...extraFeatures, ...features(limits, clientPrice)].map((f) => (
          <li key={f.text} className={f.muted ? 'is-muted' : ''}>
            <Icon name={f.icon} size={15} />
            <span>{f.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AlwaysFree() {
  return (
    <section className="card card--pad-lg stack-sm" aria-labelledby="pricing-always">
      <h2 className="section-title" id="pricing-always">{t('pricing.always.title')}</h2>
      <ul className="pricing__always">
        <li><Icon name="eye" size={15} /><span>{t('pricing.always.open')}</span></li>
        <li>
          <Icon name="sparkle" size={15} />
          <span>{t('pricing.always.try')} · <a className="text-link" href="/try">{t('pricing.always.tryLink')}</a></span>
        </li>
      </ul>
    </section>
  );
}
